import pluralize from 'pluralize';
import { useState } from 'react';

import type { TNode } from '@revelio/ui';

import { useToast } from '../../hooks/useToast';

export const useTreeSelections = ({
  initialSelections = [],
  disallowMultiLevelSelect = false,
  maxSelections,
  maxTopLevelSelections,
  minSelections = 0,
  filterLabel,
  replaceSingleSelections = false,
}: {
  initialSelections?: string[];
  disallowMultiLevelSelect?: boolean;
  maxSelections?: number;
  maxTopLevelSelections?: number;
  minSelections?: number;
  filterLabel?: string;
  replaceSingleSelections?: boolean;
}) => {
  const [selections, setSelections] = useState<string[]>(initialSelections);
  const [selectedNodeData, setSelectedNodeData] = useState<TNode[]>([]);

  const toast = useToast();

  const showMaxSelectionsWarning = (
    selectionLimit: number,
    isTopLevelFilterWarning = false
  ) => {
    if (!toast.isActive('max-selections-toast')) {
      toast({
        id: 'max-selections-toast',
        position: 'top-right',
        status: 'warning',
        title: `${filterLabel ? filterLabel : 'Selection'} Limit`,
        description: `You can only choose up to ${selectionLimit} ${isTopLevelFilterWarning ? 'top-level ' : ''}${filterLabel ? pluralize(filterLabel, selectionLimit) : 'items'} at a time!`,
        isClosable: true,
        variant: 'subtle',
      });
    }
  };

  const showMinSelectionsWarning = () => {
    if (!toast.isActive('min-selections-toast')) {
      toast({
        id: 'min-selections-toast',
        position: 'top-right',
        status: 'warning',
        title: `${filterLabel ? filterLabel : 'Selection'} Limit`,
        description: `You must make at least ${minSelections} ${filterLabel ? pluralize(filterLabel, minSelections) : 'item'}!`,
        isClosable: true,
        variant: 'subtle',
      });
    }
  };

  /**
   * Returns whether the change was applied. An add that would breach a
   * selection cap is refused with a warning instead, and callers that count
   * adds must not count those.
   */
  const onChange = (value: string[], node?: TNode[]): boolean => {
    const lastValue = value[value.length - 1];

    if (disallowMultiLevelSelect && lastValue) {
      const lastValueLevel = lastValue.split('_').length;
      const allSameLevel = value.every(
        (v) => v.split('_').length === lastValueLevel
      );

      if (!allSameLevel) {
        setSelections([lastValue]);

        if (node) {
          setSelectedNodeData(node.filter((n) => value.includes(n.id)));
        }

        return true;
      }
    }

    const isAddingSelection = value.length > selections.length;
    if (maxSelections && isAddingSelection && value.length > maxSelections) {
      if (maxSelections === 1 && replaceSingleSelections) {
        const lastValue = value[value.length - 1];
        if (!lastValue) return false;
        setSelections([lastValue]);
        if (node) {
          const lastNode = node.find((n) => n.id === lastValue);
          setSelectedNodeData(lastNode ? [lastNode] : []);
        }
        return true;
      }
      showMaxSelectionsWarning(maxSelections);
      return false;
    }

    // Enforce max top-level selections only when adding: if we're over the cap
    // after a new selection, block the change and show a warning (we don't trim
    // when removing, since that would be surprising).
    if (maxTopLevelSelections && isAddingSelection) {
      const numberOfTopLevelFilters = value.filter(
        (filterNodeValue) => filterNodeValue.split('_').length === 1
      ).length;

      if (numberOfTopLevelFilters > maxTopLevelSelections) {
        showMaxSelectionsWarning(maxTopLevelSelections, true);
        return false;
      }
    }
    setSelections(value);

    if (node) {
      setSelectedNodeData((prev) =>
        [...prev, ...node].filter((n) => value.includes(n.id))
      );
    }
    return true;
  };

  const onClear = () => {
    setSelections([]);
  };

  const validateMinSelections = (): boolean => {
    if (selections.length < minSelections) {
      showMinSelectionsWarning();
      return false;
    }
    return true;
  };

  return {
    selections,
    setSelections,
    onChange,
    onClear,
    validateMinSelections,
    showMaxSelectionsWarning,
    showMinSelectionsWarning,
    selectedNodeData,
  };
};
